<script lang="ts">
	import cross from "@core/assets/icons/close.svg?raw";
	import type { OrderProduct } from "@core/schema/OrderProduct.js";
	import type { Product } from "@core/schema/Product.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { reportAddedProductToCart, reportRemovedProductFromCart } from "../UniversalAnalytics.js";
	import { getCartStore } from "../contexts/cartContextKey.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import Button from "./common/Button.svelte";
	import Icon from "./common/Icon.svelte";
	import Media from "./common/Media.svelte";
	import { ButtonVariant } from "./common/getButtonClass.js";
	import NumberInput from "./form/NumberInput.svelte";

	const {
		orderProduct,
		index,
		products,
		maxQuantity,
	}: {
		orderProduct: OrderProduct;
		index: number;
		products: Product[];
		maxQuantity?: number;
	} = $props();

	const { tenant } = getGlobalTemplateProps();
	const cartStore = getCartStore();

	const photos = products.find((p) => p.id === orderProduct.productId)?.images ?? [];
	let quantity = $derived(orderProduct.quantity);

	async function onAmountChange(newQuantity: number): Promise<void> {
		const oldQuantity = quantity;
		if (newQuantity < 1) {
			// eslint-disable-next-line no-alert
			if (confirm("Opravdu chcete odebrat tento produkt?")) {
				await cartStore.removeProduct(index);
			} else {
				const { activeElement } = document;
				if (activeElement instanceof HTMLElement) {
					activeElement.blur();
				}
				quantity = oldQuantity;
				await cartStore.setProductQuantity(index, oldQuantity);
			}
		} else {
			quantity = newQuantity;
			await cartStore.setProductQuantity(index, newQuantity);
		}
		if (newQuantity > oldQuantity) {
			reportAddedProductToCart(orderProduct, newQuantity - oldQuantity);
		} else {
			reportRemovedProductFromCart(orderProduct, oldQuantity - newQuantity);
		}
	}

	const allProductPrice = $derived(orderProduct.price * orderProduct.quantity);

	$effect((): void => {
		if (maxQuantity !== undefined && quantity > maxQuantity) {
			quantity = maxQuantity;
			void cartStore.setProductQuantity(index, maxQuantity);
		}
	});
</script>

<div class="flex justify-between p-2">
	<div class="flex flex-col-reverse md:flex-row gap-3">
		{#each photos as photo, index (index)}
			{#if index < 1}
				<Media
					source={photo}
					width={500}
					height={0}
					alt="Přenosné ohniště Novakon"
					class={["rounded-md size-18 md:size-24 object-cover", index === 0 ? "" : "hidden md:block"]}
				/>
			{/if}
		{/each}
		<div class="flex flex-col">
			<span class="text-2md font-bold">{orderProduct.name}</span>
		</div>
	</div>

	<div class="flex flex-col items-end gap-2">
		<Button
			variant={ButtonVariant.NoDesign}
			onclick={async (): Promise<void> => {
				await cartStore.removeProduct(index);
			}}
		>
			<Icon icon={cross} />
		</Button>

		<div class="flex flex-col lg:flex-row items-end gap-3">
			<div class="flex gap-1">
				<NumberInput
					class="md:contents w-fit"
					inputClass="!text-md !w-10"
					id="product.{index}.quantity"
					max={maxQuantity}
					value={quantity}
					oninput={onAmountChange}
				/>
			</div>
			<div class="text-md font-bold min-w-32 text-right">
				{formatPrice(allProductPrice, tenant.locale, true)}
			</div>
		</div>
	</div>
</div>
<hr class="my-1 md:col-span-6 border-nov-light-200" />

{#each orderProduct.gifts ?? [] as gift (gift.productId)}
	{@const giftPhotos = products.find((p) => p.id === gift.productId)?.images ?? []}
	<div class="flex justify-between p-2">
		<div class="flex gap-3 flex-col-reverse md:flex-row">
			{#each giftPhotos as photo, index (index)}
				{#if index < 1}
					<Media
						source={photo}
						width={500}
						height={0}
						alt="Přenosné ohniště Novakon"
						class={["rounded-md size-18 md:size-24 object-cover", index === 0 ? "" : "hidden md:block"]}
					/>
				{/if}
			{/each}
			<div class="flex flex-col">
				<span class="text-2md/tight font-bold">{gift.name}</span>
				<span>Dárek</span>
			</div>
		</div>

		<div class="flex flex-col items-end justify-between gap-2">
			<Button variant={ButtonVariant.NoDesign} disabled>
				<Icon icon={cross} />
			</Button>

			<div class="flex flex-col lg:flex-row items-end gap-3">
				<div class="flex gap-1">
					<NumberInput
						class="md:contents w-fit"
						inputClass="!text-md !w-10"
						id="product.{index}.quantity"
						max={maxQuantity}
						value={quantity}
						disabled
					/>
				</div>
				<div class="text-md font-bold min-w-32 text-right">Zdarma</div>
			</div>
		</div>
	</div>
	<hr class="my-1 md:col-span-6 border-nov-light-200" />
{/each}
